import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import PageWrapper from "@/components/PageWrapper";
import SectionHeading from "@/components/SectionHeading";
import Particles from "@/components/Particles";
import { SERVICE_DETAILS } from "@/data/services";

const ORDER = ["shopify", "wordpress", "ecommerce", "custom", "design", "maintenance"];

export default function Services() {
  return (
    <PageWrapper>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh" aria-hidden />
        <Particles count={24} />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-20 md:pt-28 pb-20 md:pb-32">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-400">
            Services
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mt-5 font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight text-white max-w-5xl"
          >
            Digital Solutions <br className="hidden md:block" /> Designed To{" "}
            <span className="text-gradient-accent">Scale.</span>
          </motion.h1>
          <p className="mt-7 max-w-2xl text-zinc-400 text-base md:text-lg leading-relaxed">
            Whether you need a Shopify storefront, a WordPress platform, or a
            fully custom web application, we deliver experiences engineered for
            growth.
          </p>
        </div>
      </section>

      {/* SERVICES DETAIL */}
      <section className="relative border-t border-white/10">
        {ORDER.map((key, idx) => {
          const s = SERVICE_DETAILS[key];
          const Icon = s.icon;
          const reverse = idx % 2 === 1;
          return (
            <div
              key={key}
              data-testid={`service-detail-${key}`}
              className="relative border-b border-white/10 py-20 md:py-28"
            >
              <div
                className={`mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center ${
                  reverse ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="lg:col-span-7">
                  <div className="w-12 h-12 grid place-items-center rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400">
                    <Icon size={20} />
                  </div>
                  <p className="text-xs uppercase tracking-[0.25em] text-blue-400 mt-6">
                    0{idx + 1} / 0{ORDER.length}
                  </p>
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-white leading-[1.05]"
                  >
                    {s.title}
                  </motion.h2>
                  <p className="mt-5 max-w-xl text-zinc-400 text-base md:text-lg leading-relaxed">
                    {s.description}
                  </p>
                  <div className="mt-8">
                    <Link
                      to="/contact"
                      data-testid={`service-detail-cta-${key}`}
                      className="inline-flex items-center gap-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium px-6 py-3 transition-colors"
                    >
                      Discuss your {s.title.toLowerCase()} <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-7 md:p-8">
                    <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">
                      What&apos;s included
                    </p>
                    <ul className="mt-5 space-y-3">
                      {s.items.map((it) => {
                        const ItIcon = it.icon;
                        return (
                          <li
                            key={it.label}
                            className="flex items-center gap-3 text-zinc-200"
                          >
                            <span className="w-9 h-9 grid place-items-center rounded-lg bg-white/5 border border-white/10 text-blue-300">
                              <ItIcon size={16} />
                            </span>
                            <span className="text-base">{it.label}</span>
                            <Check
                              size={14}
                              className="ml-auto text-blue-400"
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32">
        <div className="mx-auto max-w-5xl px-6 md:px-10 text-center">
          <SectionHeading
            align="center"
            title="Not sure which path is right?"
            description="Tell us about your business — we&apos;ll recommend the right approach in plain language, with no hard sell."
          />
          <div className="mt-10">
            <Link
              to="/contact"
              data-testid="services-bottom-cta"
              className="inline-flex items-center gap-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white text-sm md:text-base font-medium px-7 py-4 transition-colors shadow-[0_10px_40px_-10px_rgba(59,130,246,0.7)]"
            >
              Book Free Consultation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
